import { Controller, useFormContext } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Filter, ShoppingCart } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { EditorSection, Field } from '~/components/promotion/editor-section';
import { ToggleRow } from '~/components/toggle-row';
import type { PromotionFormValues } from '~/lib/schemas/promotion';

type NullableNumberName = 'minAmount' | 'maxAmount' | 'minQuantity';

/** Nullable numeric field (empty input → null), optional € prefix. */
function NullableNumberField({
	control,
	name,
	prefix
}: {
	control: Control<PromotionFormValues>;
	name: NullableNumberName;
	prefix?: string;
}) {
	return (
		<Controller
			control={control}
			name={name}
			render={({ field }) => (
				<div className="relative">
					{prefix && (
						<span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-foreground-subtle">{prefix}</span>
					)}
					<Input
						type="number"
						className={prefix ? 'pl-6' : undefined}
						value={field.value ?? ''}
						onChange={e => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
					/>
				</div>
			)}
		/>
	);
}

export function CartThresholdsSection() {
	const { t } = useTranslation();
	const { control } = useFormContext<PromotionFormValues>();

	return (
		<EditorSection
			title={t('promotion.conditions.cartThresholds.title')}
			subtitle={t('promotion.conditions.cartThresholds.subtitle')}
			icon={ShoppingCart}>
			<div className="mb-4 grid grid-cols-3 gap-4">
				<Field label={t('promotion.conditions.cartThresholds.minAmount')} help={t('promotion.conditions.cartThresholds.minAmountHelp')}>
					<NullableNumberField control={control} name="minAmount" prefix="€" />
				</Field>
				<Field label={t('promotion.conditions.cartThresholds.maxAmount')} help={t('promotion.conditions.cartThresholds.maxAmountHelp')}>
					<NullableNumberField control={control} name="maxAmount" prefix="€" />
				</Field>
				<Field label={t('promotion.conditions.cartThresholds.minQuantity')} help={t('promotion.conditions.cartThresholds.minQuantityHelp')}>
					<NullableNumberField control={control} name="minQuantity" />
				</Field>
			</div>
			<Controller
				control={control}
				name="appliesToSelectedProducts"
				render={({ field }) => (
					<ToggleRow
						label={t('promotion.conditions.cartThresholds.selectedOnly')}
						help={t('promotion.conditions.cartThresholds.selectedOnlyHelp')}
						icon={Filter}
						checked={field.value}
						onCheckedChange={field.onChange}
					/>
				)}
			/>
		</EditorSection>
	);
}
